import { Component, Input, NgZone, OnInit } from '@angular/core';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { KpiDataObj, Views, getObjectiveColor } from './kpi.model';
import { ErrorCode } from "src/shared/models/error-code.model";
import { KpiService } from './kpi.service';
import { ObjectivesDetailsService } from '../../../reusables/objectives-details/objectives-details.service';

@Component({
    selector: 'cv-kpi',
    templateUrl: './kpi.component.html',
    styleUrls: ['./kpi.component.scss']
})
export class KpiComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    isFirstLoad: boolean;
    /** this is a lagged version of the data pull to see if the ammount of metrics selected have changed, if so it needs to resize */
    objs: KpiDataObj[] = [];
    dataLoadSuccess = false;
    preventLoadingModal = true;
    view = Views.Horizontal;
    oldView = Views.Horizontal;
    states = {
        data: {
            loading: false,
            success: false
        },
        image: {
            loading: false,
            success: false
        }
    };
    isNoMetric = false;
    isDateRangeTooWideIntraDay = false;
    isCXOne = false;
    noData = false;
    cWidth = 0;
    hasState = false; // Updated to true after the state has been saved at least once for PM
    localizedString = "";
    forceResize = false;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private service: KpiService,
        private zone: NgZone,
        private config: CvConfigurationService,
        private objectivesDetailsService: ObjectivesDetailsService,
    ) {
        this.isCXOne = this.config.isCxOne;
    }

    async ngOnInit() {
        this.isFirstLoad = true;
        const localizationPromises = [
            this.cvLocalizationService.GetSection("KPI_Module"),
            this.cvLocalizationService.GetSection("Common"),
            this.cvLocalizationService.GetSection("Objectives"),
        ];
        await Promise.all(localizationPromises);
        this.localizedString = this.cvLocalizationService.getCachedText("Common", "Original_Metric");
        this.setComboMenu();
        this.forceResize = this.moduleManager.container.module?.fromTaskbar;

        this.moduleManager.setOptions({
            widgetMode: false,
            onSettingsApplied: settings => this.zone.run(() => this.loadData(false)),
            hasRefresh: true,
            onRefresh: isAutoRefresh => this.zone.run(() => this.loadData(isAutoRefresh)),
            canRefresh: () => this.states.data.success,
            onExport: (format, isCXOne) => this.zone.run(() => this.service.LoadDataForExport(this.moduleKey, format, isCXOne)),
            getTransferObject: () => ({view: this.view}),
            onTransferLoaded: (transferObject) => {
                this.view = transferObject.view;
                this.oldView = transferObject.view;
                this.moduleManager.saveState();
            },
        });


        this.moduleManager.addState({
            name: 'viewModeKPI',
            autoLoad: true,
            getStateData: () => this.view,
            onStateLoaded: (stateData) => {
                if (stateData == null)
                {
                    return;
                }
                this.view = stateData;
                this.oldView = stateData;
            },
        });
    }

    async loadData(isAutoRefresh: boolean): Promise<void> {
        if (this.states.data.loading) {
            return;
        }
        this.states.data.loading = true;
        const $item = this.moduleManager?.container?.gridsterItem?.$item;
        this.moduleManager.preventModuleMapUpdate();

        try {
            const data = await this.service.LoadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.states.data.loading = false;
            this.noData = this.objs.length === 0;

            if (data.Error === ErrorCode.NoMetric) {
                this.isNoMetric = true;
                //if the this.objs has previous items added, reset the this.objs array to 0
                if(this.objs?.length > 0){
                    this.objs = [];
                }

                if (!this.isCXOne) {
                    this.setCustomSize(this.view);
                }
                else {
                    if (!this.isFirstLoad) {
                        this.setCustomSize(this.view);
                    }
                }
            }
            else if (data.Error === ErrorCode.DateRangeTooWideIntraday) {
                this.isDateRangeTooWideIntraDay = true;
            }else if (data.Error === ErrorCode.NullMetricData) {
                this.noData = true;
            } else {
                this.isNoMetric = false;
                this.isDateRangeTooWideIntraDay = false;
                this.noData = false;
                if (this.objs?.length !== data.metrics.length) {
                    this.objs = data.metrics;
                    if (!this.isFirstLoad) {
                        this.setCustomSize(this.view);
                    }
                }
                else {
                    this.objs = data.metrics;
                }
                if(!this.isCXOne){
                    this.objs.forEach(obj => {
                        obj.color = getObjectiveColor(obj.color);
                    });
                    if (!this.hasState || this.forceResize)
                        this.setCustomSize(this.view);
                }
                this.preventLoadingModal = false;
                this.moduleManager.setAutoRefreshReady();
            }
            this.states.data.success = true;
            this.dataLoadSuccess = true;
        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
            this.states.data.loading = false;
        } finally {
            this.moduleManager.removeModuleMapPrevention();
        }
        this.hasState = true;
        this.isFirstLoad = false;
    }

    isLoading(): boolean {
        if (this.states.data.loading || this.states.image.loading) // Append other states here when added
            return true;
        else
            return false;
    }

    async setComboMenu() {
        this.moduleManager.addMenuItem({
            name: 'Widget Display',
            icon: 'widgetType',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess && !this.preventLoadingModal,
            readOnlyEnabled: this.isCXOne,
            subItems: [{
                name: this.cvLocalizationService.getCachedText("KPI_Module", "View_Horizontal"),
                icon: 'horizontal',
                click: () => {
                    this.zone.run(() => {
                        this.view = Views.Horizontal;
                        this.setCustomSize(this.view);
                        this.moduleManager.saveState();
                    });
                }
            },
            {
                name: this.cvLocalizationService.getCachedText("KPI_Module", "View_Vertical"),
                icon: 'vertical',
                click: () => {
                    this.zone.run(() => {
                        this.view = Views.Vertical;
                        this.setCustomSize(this.view);
                        this.moduleManager.saveState();
                    });
                }
            }]
        } as ModuleManagerSubMenu);
    }

    setCustomSize(viewMode: number): void {
        let switchColsAndRows = false;
        if (viewMode !== this.oldView) {
            switchColsAndRows = true;
        }
        // Currently CXone is the same size, but split here incase it needs to be different for PM later.
        const colSize0 = 3;
        let colSize1 =  this.config.isCxOne ? 5 : 4;
        const rowSize0 = this.config.isCxOne ? 5 : 3;
        const rowSize1 = this.config.isCxOne ? 3 : 2;

        if(this.isNoMetric) {
            this.moduleManager.setSize(colSize1, rowSize0, true);
            this.forceResize = false;
            return;
        }
        else if (!this.states.data.loading) {
            const nameCanvas = document.createElement('canvas');
            const ctx1 = nameCanvas.getContext('2d');
            ctx1.font = this.isCXOne ? '22px sans-sarif' : '14px sans-sarif';

            const test2 = document.createElement('canvas');
            const ctx2 = test2.getContext('2d');
            ctx2.font = this.isCXOne ? '32px sans-sarif' : '14px sans-sarif';

            const colWidth = parseFloat(this.moduleManager.container.gridsterItem?.width.toString()) /
            parseFloat(this.moduleManager.container.gridsterItem?.$item.cols.toString());

            if (viewMode === Views.Horizontal || viewMode == null) {
                this.cWidth = 0;

                this.objs.forEach(obj => {
                    if (ctx1.measureText(obj.metric.Abbreviation).width ?? 0 >
                    ctx2.measureText(obj.metricData.MetricDisplayValue).width ?? 0) {
                        const abbTextWidth = Math.ceil((ctx1.measureText(obj.metric.Abbreviation).width + 40) / colWidth) ?? 0;
                        this.cWidth += abbTextWidth > colSize1 ? abbTextWidth : colSize1;
                    }
                    else {
                        const mdvTextWidth = Math.ceil((ctx2.measureText(obj.metricData.MetricDisplayValue).width + 40) / colWidth)
                        ?? 0;
                        this.cWidth += mdvTextWidth > colSize1 ? mdvTextWidth : colSize1;
                    }
                });
                // horizontal view
                if ((Object.keys(this.moduleManager.loadedStateValues).length === 0 && this.isFirstLoad) || this.forceResize) { // when the widget is initially loaded calculate size based on number of metrics
                    this.moduleManager.setSize(this.cWidth > this.moduleManager.container.gridsterItem?.gridster.$options.maxCols
                        ? this.moduleManager.container.gridsterItem?.gridster.$options.maxCols : this.cWidth, rowSize0, true);
                } else if (switchColsAndRows) { // flip rows and columns when changing views
                    this.moduleManager.setSize(this.moduleManager.container.gridsterItem?.$item.rows,
                        this.moduleManager.container.gridsterItem?.$item.cols, true);
                } else { // widget has state and shouldn't resize
                    this.moduleManager.setSize(this.moduleManager.container.gridsterItem?.$item.cols,
                        this.moduleManager.container.gridsterItem?.$item.rows, true);
                }
            } else {
                this.objs.forEach(obj => {
                    if (ctx1.measureText(obj.metric.Abbreviation).width ?? 0 >
                    ctx2.measureText(obj.metricData.MetricDisplayValue).width ?? 0) {
                        const abbTextWidth = Math.ceil((ctx1.measureText(obj.metric.Abbreviation).width + 40) / colWidth) ?? 0;
                        if (abbTextWidth > colSize1) colSize1 = abbTextWidth;
                    }
                    else {
                        const mdvTextWidth = Math.ceil((ctx2.measureText(obj.metricData.MetricDisplayValue).width + 40) / colWidth)
                        ?? 0;
                        if (mdvTextWidth > colSize1) colSize1 = mdvTextWidth;
                    }
                });
                //vertical view
                if ((Object.keys(this.moduleManager.loadedStateValues).length === 0 && this.isFirstLoad) || this.forceResize){ // when the widget is initially loaded calculate size based on number of metrics
                    this.moduleManager.setSize(colSize1, (this.objs.length * rowSize1) + 1, true);
                } else if (switchColsAndRows) { // flip rows and columns when changing views
                    this.moduleManager.setSize(this.moduleManager.container.gridsterItem?.$item.rows,
                        this.moduleManager.container.gridsterItem?.$item.cols ,true);
                } else { // widget has state and shouldn't resize
                    this.moduleManager.setSize(this.moduleManager.container.gridsterItem?.$item.cols,
                        this.moduleManager.container.gridsterItem?.$item.rows, true);
                }
            }
        }
        this.oldView = viewMode;
        this.forceResize = false;
    }

    hasSpacesItemName(text: any): boolean{
        let hasSpaces = false;
        if(text !== '' && text != null && text.length > 0){
            hasSpaces = text.trim().indexOf(' ') >= 0;
        }
        return hasSpaces;
    }

    getMetricDisplayName(obj)
    {
        return this.getIsMetricRenamed(obj) ?
            obj.metric.MetricCustomName + "*" : obj.metric.Abbreviation;
    }

    getIsMetricRenamed(obj)
    {
        return obj.metric.MetricCustomName && obj.metric.MetricCustomName !== '';
    }

    getMetricToolTip(obj)
    {
        return this.getIsMetricRenamed(obj) ?
            this.localizedString +": " + "\n" + obj.metric.Abbreviation : obj.metric.Abbreviation;
    }

    openObjectivesDetailsModal(kpiObject: KpiDataObj){
        this.objectivesDetailsService.openObjectivesDetailsModal(
            kpiObject.metricData,
            null,
            kpiObject.metric.MetricName,
            true
        );
    }
}
